import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type { StoreActionLoggableName } from '../chat/types';

export type StoreActionExecutionError = {
	actionLoggableName: StoreActionLoggableName;
};

export type StoreError = {
	type: 'ERROR';
	content: string;
	time_created: string;
	id: string;
	status_code?: number;
	message_template?: string;
	timeout?: number;
	errorType?: string;
	traceId?: string;
	entryPoint?: string;
	storeActionExecutionError?: StoreActionExecutionError;
	errorName?: string;
};

export type ChatStatus = 'online' | 'offline' | 'launching';

export type RateLimitedActionType = 'deep-research' | 'generic';

/**
 * Union type representing different kinds of conversation-level errors that can occur.
 * This replaces the separate error and rate limiting fields to provide a more unified error handling approach.
 */
export type ConversationError =
	| {
			type: 'conversationNotFound';
			underlyingError: StoreError;
	  }
	| {
			type: 'conversationEmptyOrNotPresentInStore';
			underlyingError: StoreError;
	  }
	| {
			type: 'rateLimited';
			underlyingError: StoreError;
			rateLimitedActionType: RateLimitedActionType;
	  }
	| {
			type: 'hipaa';
			underlyingError: StoreError;
	  }
	| {
			type: 'generic';
			underlyingError: StoreError;
	  };

/**
 * Check if a ConversationError represents a rate limiting error
 */
export function isRateLimited(
	error: ConversationError | undefined,
): error is ConversationError & { type: 'rateLimited' } {
	return error?.type === 'rateLimited';
}

/**
 * Check if the error should show as a full-screen error vs a banner
 */
export function isFullScreenError(error: ConversationError | undefined): boolean {
	return error
		? ['conversationNotFound', 'conversationEmptyOrNotPresentInStore', 'hipaa', 'generic'].includes(
				error.type,
			)
		: false;
}

/**
 * Get the rate limited action type from a ConversationError, if it's a rate limited error
 */
export function getRateLimitedActionType(
	error: ConversationError | undefined,
): RateLimitedActionType | undefined {
	return isRateLimited(error) ? error.rateLimitedActionType : undefined;
}

const store = createStore({
	name: 'rovoChatSignals',
	initialState: {
		conversationError: undefined as ConversationError | undefined,
		status: 'launching' as ChatStatus,
	},
	actions: {
		setConversationError:
			(
				conversationError: ConversationError | undefined,
			): (({
				setState,
			}: StoreActionApi<{
				conversationError: ConversationError | undefined;
				status: ChatStatus;
			}>) => void) =>
			({ setState }) => {
				setState({ conversationError });
			},
		setStatus:
			(
				status: ChatStatus,
			): (({
				setState,
			}: StoreActionApi<{
				conversationError: ConversationError | undefined;
				status: ChatStatus;
			}>) => void) =>
			({ setState }) => {
				setState({ status });
			},
	},
});

export const useChatSignalsActions: HookActionsFunction<
	BoundActions<
		{
			conversationError: ConversationError | undefined;
			status: ChatStatus;
		},
		{
			setConversationError: (conversationError: ConversationError | undefined) => ({
				setState,
			}: StoreActionApi<{
				conversationError: ConversationError | undefined;
				status: ChatStatus;
			}>) => void;
			setStatus: (status: ChatStatus) => ({
				setState,
			}: StoreActionApi<{
				conversationError: ConversationError | undefined;
				status: ChatStatus;
			}>) => void;
		}
	>
> = createActionsHook(store);

export const useChatSignals: HookFunction<
	{
		conversationError: ConversationError | undefined;
		status: ChatStatus;
	},
	BoundActions<
		{
			conversationError: ConversationError | undefined;
			status: ChatStatus;
		},
		{
			setConversationError: (conversationError: ConversationError | undefined) => ({
				setState,
			}: StoreActionApi<{
				conversationError: ConversationError | undefined;
				status: ChatStatus;
			}>) => void;
			setStatus: (status: ChatStatus) => ({
				setState,
			}: StoreActionApi<{
				conversationError: ConversationError | undefined;
				status: ChatStatus;
			}>) => void;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => ({
		conversationError: state.conversationError,
		status: state.status,
	}),
});
